import { fieldChannelIds } from './field';
import { beltYardGuidePath, canyonGuidePath, FIELD_SPECS, grandVortexGuidePath, vortexGuidePath } from './field-maps';
import { seededRandom } from './random';
import { towerGuidePath } from './tower-map';
import { gravityBowlsGuidePath } from './gravity-bowls-map';
import { RING_CIRCUIT_FIELD, ringCircuitGuidePath } from './ring-circuit-map';
import { PINBALL_EXPANSION, PINBALL_V3_FIELD } from './pinball-map';
import type { FateMapDefinition, FateMapId, FateMapSelection, TrackPoint } from './types';

export const FATE_WORLD_WIDTH = 1400;
export const FATE_WORLD_HEIGHT = 2800;
export const PATH_GATE_EVENT_SHIFT = 32;

// 보관 맵도 물리 회귀 검사와 기존 리플레이의 명시적 id 조회에는 남긴다.
// 새 경기의 선택 UI·랜덤 추첨은 아래 FATE_MAPS만 사용한다.
const ALL_FATE_MAPS: readonly FateMapDefinition[] = [
  {
    id: 'classic',
    version: 'classic-v3.4',
    title: '클래식 서킷',
    watchPoint: 'S커브·범퍼 광장',
    description: '연속 S커브와 벽타기 차단 범퍼, 교차 회전봉이 번갈아 주행선을 섞는 기본 트랙',
    background: '#0a0f26',
    track: '#39418c',
    accent: '#ffd449',
    worldWidth: 1200,
    worldHeight: 5000,
    firstFinishSeconds: 28.6,
    lastFinishSeconds: 45,
    path: [
      { x: 600, y: 120, width: 520 }, { x: 600, y: 430, width: 480 },
      { x: 540, y: 740, width: 430 }, { x: 600, y: 1020, width: 320 },
      { x: 500, y: 1280, width: 210 }, { x: 400, y: 1540, width: 380 },
      { x: 450, y: 1820, width: 460 }, { x: 610, y: 2100, width: 470 },
      { x: 730, y: 2380, width: 430 }, { x: 820, y: 2660, width: 330 },
      { x: 760, y: 2940, width: 280 }, { x: 870, y: 3220, width: 260 },
      { x: 800, y: 3460, width: 190 }, { x: 660, y: 3700, width: 360 },
      { x: 500, y: 3970, width: 420 }, { x: 350, y: 4250, width: 400 },
      { x: 470, y: 4550, width: 320 }, { x: 720, y: 4990, width: 168 },
    ],
    landmarks: [
      { progress: .17, type: 'junction', label: 'S커브 핀' },
      { progress: .3, type: 'gate', label: '첫 회전봉' },
      { progress: .49, type: 'bumper', label: '충돌 광장' },
      { progress: .63, type: 'junction', label: '엇갈림 관문' },
      { progress: .77, type: 'booster', label: '부스터 슬랄럼' },
      { progress: .91, type: 'funnel', label: '결승 깔때기' },
    ],
    obstacles: [
      { id: 'classic-entry-peg-a', type: 'peg', progress: .11, lateral: -76, radius: 20 },
      { id: 'classic-entry-peg-b', type: 'peg', progress: .145, lateral: 78, radius: 20 },
      { id: 'classic-entry-bumper', type: 'bumper', progress: .19, lateral: -12, radius: 26 },
      { id: 'classic-spinner-entry', type: 'spinner', progress: .255, lateral: 0, length: 210, thickness: 12, speed: -1.45, phase: .52, arms: 2 },
      { id: 'classic-entry-afterspin', type: 'bumper', progress: .285, lateral: 105, radius: 22 },
      { id: 'classic-chicane-a', type: 'bumper', progress: .33, lateral: -104, radius: 25 },
      { id: 'classic-chicane-b', type: 'bumper', progress: .365, lateral: 96, radius: 25 },
      { id: 'classic-spinner-cross', type: 'spinner', progress: .405, lateral: 0, length: 350, thickness: 12, speed: 1.18, phase: 1.05, arms: 2 },
      { id: 'classic-bumper-a', type: 'bumper', progress: .44, lateral: -58, radius: 27 },
      { id: 'classic-bumper-b', type: 'bumper', progress: .455, lateral: 70, radius: 27 },
      { id: 'classic-bumper-c', type: 'bumper', progress: .495, lateral: -18, radius: 29 },
      { id: 'classic-bumper-d', type: 'bumper', progress: .545, lateral: -92, radius: 24 },
      { id: 'classic-bumper-e', type: 'bumper', progress: .545, lateral: 92, radius: 24 },
      { id: 'classic-plaza-exit', type: 'bumper', progress: .575, lateral: -20, radius: 24 },
      { id: 'classic-gate-left', type: 'gate', progress: .625, lateral: -96, length: 70, radius: 11, pairId: 'classic-gate' },
      { id: 'classic-gate-right', type: 'gate', progress: .625, lateral: 96, length: 70, radius: 11, pairId: 'classic-gate' },
      { id: 'classic-spinner-a', type: 'spinner', progress: .665, lateral: 0, length: 198, thickness: 14, speed: 1.55, phase: 0 },
      { id: 'classic-slalom-a', type: 'bumper', progress: .705, lateral: -105, radius: 24 },
      { id: 'classic-spinner-slalom', type: 'spinner', progress: .745, lateral: 0, length: 330, thickness: 12, speed: -1.62, phase: .82, arms: 2 },
      { id: 'classic-slalom-b', type: 'bumper', progress: .785, lateral: 110, radius: 23 },
      { id: 'classic-slalom-c', type: 'bumper', progress: .815, lateral: -105, radius: 25 },
      { id: 'classic-slalom-exit', type: 'peg', progress: .835, lateral: 90, radius: 20 },
      { id: 'classic-spinner-b', type: 'spinner', progress: .86, lateral: 0, length: 286, thickness: 13, speed: -1.35, phase: 1.57, arms: 2 },
      { id: 'classic-finish-catcher', type: 'peg', progress: .895, lateral: 80, radius: 22 },
    ],
    zones: [
      { id: 'classic-entry-chicane', type: 'collision', start: .08, end: .38, strength: .7, label: 'S커브 핀' },
      { id: 'classic-plaza', type: 'collision', start: .41, end: .59, strength: 1, label: '충돌 광장' },
      { id: 'classic-crossflow', type: 'collision', start: .67, end: .9, strength: 1, label: '교차 슬랄럼' },
      { id: 'classic-boost', type: 'boost', start: .73, end: .82, strength: .23, label: '부스터 웨이브' },
      { id: 'classic-slow', type: 'slow', start: .84, end: .9, strength: .1, label: '슬로우 존' },
      { id: 'classic-finish', type: 'finish', start: .92, end: 1, strength: .18 },
    ],
    cameraZones: [
      { id: 'start', start: 0, end: .2, zoom: 1.05 },
      { id: 'collision', start: .39, end: .66, zoom: 1.2 },
      { id: 'finish', start: .9, end: 1, zoom: 1.35 },
    ],
    eventPool: ['gate', 'boost', 'reverse', 'slow'],
  },
  {
    id: 'conveyor',
    version: 'conveyor-v1.5',
    title: '컨베이어 팩토리',
    watchPoint: '3레인·C급행',
    description: '길고 꼬불꼬불한 A·B 생산 레인과 대기 후 빠르게 합류하는 C 급행이 갈리는 공장 트랙',
    background: '#0a0f26',
    track: '#39418c',
    accent: '#ffd449',
    worldWidth: 1600,
    worldHeight: 900,
    firstFinishSeconds: 30,
    lastFinishSeconds: 46,
    path: [
      { x: 110, y: 450, width: 340 }, { x: 300, y: 450, width: 330 },
      { x: 500, y: 450, width: 330 }, { x: 700, y: 450, width: 330 },
      { x: 805, y: 450, width: 340 }, { x: 920, y: 450, width: 150 },
      { x: 1010, y: 450, width: 130 }, { x: 1160, y: 450, width: 150 },
      { x: 1315, y: 450, width: 140 }, { x: 1420, y: 450, width: 300 },
      { x: 1534, y: 450, width: 64 }, { x: 1560, y: 450, width: 56 },
    ],
    landmarks: [],
    obstacles: [],
    zones: [
      { id: 'conveyor-plaza', type: 'collision', start: .34, end: .56, strength: 1, label: '재집결 광장' },
      { id: 'conveyor-spin', type: 'collision', start: .7, end: .86, strength: 1, label: '회전봉 광장' },
      { id: 'conveyor-finish', type: 'finish', start: .93, end: 1, strength: .18 },
    ],
    cameraZones: [
      { id: 'start', start: 0, end: .18, zoom: 1.06 },
      { id: 'collision', start: .3, end: .6, zoom: 1.18 },
      { id: 'finish', start: .8, end: 1, zoom: 1.32 },
    ],
    eventPool: ['belt-reverse', 'gate-close', 'shortcut-open'],
    field: FIELD_SPECS.conveyor,
  },
  {
    id: 'vortex',
    version: 'vortex-v1.1',
    title: '소용돌이 아레나',
    watchPoint: '중앙 게이트',
    description: '내향 나선을 세 바퀴 돌아 중앙 셔터가 열리는 순간 순위가 뒤집히는 원형 아레나',
    background: '#0a0f26',
    track: '#39418c',
    accent: '#7c5cff',
    worldWidth: 1300,
    worldHeight: 1300,
    firstFinishSeconds: 32,
    lastFinishSeconds: 52,
    path: vortexGuidePath(),
    landmarks: [],
    obstacles: [],
    zones: [
      { id: 'vortex-hall', type: 'collision', start: .82, end: .97, strength: 1, label: '재집결 홀' },
      { id: 'vortex-finish', type: 'finish', start: .97, end: 1, strength: .18 },
    ],
    cameraZones: [
      { id: 'start', start: 0, end: .15, zoom: .92 },
      { id: 'spiral', start: .15, end: .82, zoom: 1.05 },
      { id: 'finish', start: .85, end: 1, zoom: 1.28 },
    ],
    eventPool: ['disk-reverse', 'shutter-open', 'boost-ring'],
    field: FIELD_SPECS.vortex,
  },
  {
    id: 'seesaw',
    version: 'seesaw-v1.3',
    title: '시소 캐니언',
    watchPoint: '시소 역전',
    description: '올라탄 무게로 기우는 시소 4개가 좌우 길을 번갈아 여는 지그재그 협곡',
    background: '#0a0f26',
    track: '#39418c',
    accent: '#3ef2b6',
    worldWidth: 1200,
    worldHeight: 1500,
    firstFinishSeconds: 29,
    lastFinishSeconds: 55,
    path: [
      { x: 140, y: 140, width: 180 }, { x: 360, y: 200, width: 160 },
      { x: 400, y: 255, width: 60 }, { x: 332, y: 335, width: 60 },
      { x: 240, y: 430, width: 180 }, { x: 430, y: 470, width: 180 },
      { x: 646, y: 525, width: 60 }, { x: 714, y: 645, width: 60 },
      { x: 820, y: 740, width: 180 }, { x: 736, y: 838, width: 60 },
      { x: 664, y: 955, width: 60 }, { x: 500, y: 1040, width: 180 },
      { x: 420, y: 1145, width: 60 }, { x: 488, y: 1265, width: 60 },
      { x: 700, y: 1340, width: 160 }, { x: 946, y: 1350, width: 160 },
    ],
    landmarks: [],
    obstacles: [],
    zones: [
      { id: 'seesaw-p1', type: 'collision', start: .2, end: .38, strength: 1, label: '시소 SS1' },
      { id: 'seesaw-p2', type: 'collision', start: .44, end: .6, strength: 1, label: '시소 SS2' },
      { id: 'seesaw-finish', type: 'finish', start: .9, end: 1, strength: .18 },
    ],
    cameraZones: [
      { id: 'start', start: 0, end: .14, zoom: 1.06 },
      { id: 'collision', start: .25, end: .6, zoom: 1.18 },
      { id: 'finish', start: .85, end: 1, zoom: 1.32 },
    ],
    eventPool: ['seesaw-free', 'tilt-reverse', 'wall-open'],
    field: FIELD_SPECS.seesaw,
  },
  {
    id: 'gravity-hub',
    version: 'gravity-hub-v1.4',
    title: '중력 환승역',
    watchPoint: '전환실',
    description: '방마다 중력 방향이 다른 네 개의 방을 집결과 쏟아짐으로 갈아타는 환승역',
    background: '#0a0f26',
    track: '#39418c',
    accent: '#4e8aff',
    worldWidth: 1500,
    worldHeight: 1100,
    firstFinishSeconds: 31,
    lastFinishSeconds: 66,
    path: [
      { x: 290, y: 120, width: 300 }, { x: 290, y: 300, width: 300 },
      { x: 290, y: 400, width: 72 }, { x: 290, y: 470, width: 240 },
      { x: 340, y: 570, width: 240 }, { x: 500, y: 575, width: 240 },
      { x: 640, y: 570, width: 72 }, { x: 740, y: 570, width: 72 },
      { x: 830, y: 545, width: 260 }, { x: 885, y: 490, width: 260 },
      { x: 890, y: 430, width: 72 }, { x: 890, y: 380, width: 72 },
      { x: 1000, y: 380, width: 72 }, { x: 1100, y: 385, width: 72 },
      { x: 1180, y: 440, width: 300 }, { x: 1250, y: 520, width: 300 },
      { x: 1270, y: 590, width: 72 }, { x: 1270, y: 660, width: 72 },
      { x: 1340, y: 680, width: 72 }, { x: 1445, y: 680, width: 90 },
    ],
    landmarks: [],
    obstacles: [],
    zones: [
      { id: 'hub-b', type: 'collision', start: .24, end: .44, strength: 1, label: '방 B' },
      { id: 'hub-c', type: 'collision', start: .5, end: .68, strength: 1, label: '방 C' },
      { id: 'hub-finish', type: 'finish', start: .92, end: 1, strength: .18 },
    ],
    cameraZones: [
      { id: 'start', start: 0, end: .18, zoom: 1.05 },
      { id: 'rooms', start: .25, end: .72, zoom: 1.14 },
      { id: 'finish', start: .85, end: 1, zoom: 1.3 },
    ],
    eventPool: ['gravity-early', 'room-reverse', 'transfer-delay'],
    field: FIELD_SPECS['gravity-hub'],
  },
  {
    id: 'pinball',
    version: 'pinball-v3.5',
    title: '핀볼 리액터',
    watchPoint: '확장 핀 아레나·3갈래 낙하',
    description: '넓은 핀 아레나와 세 갈래 낙하를 지나, 합류 범퍼와 회전 관문에서 다시 뒤섞이는 고속 하강 트랙',
    background: '#0a0f26',
    track: '#39418c',
    accent: '#ff7a70',
    worldWidth: 900 * PINBALL_EXPANSION,
    worldHeight: 1600 * PINBALL_EXPANSION,
    firstFinishSeconds: 42,
    lastFinishSeconds: 50,
    path: [
      { x: 450, y: 100, width: 460 }, { x: 450, y: 240, width: 440 },
      { x: 450, y: 360, width: 220 }, { x: 450, y: 500, width: 700 },
      { x: 450, y: 700, width: 700 }, { x: 450, y: 830, width: 700 },
      { x: 451, y: 960, width: 150 }, { x: 451, y: 1120, width: 150 },
      { x: 451, y: 1230, width: 90 }, { x: 450, y: 1300, width: 98 },
      { x: 450, y: 1400, width: 220 }, { x: 450, y: 1470, width: 120 },
      { x: 450, y: 1548, width: 100 },
    ].map((value) => ({ x: value.x * PINBALL_EXPANSION, y: value.y * PINBALL_EXPANSION, width: value.width * PINBALL_EXPANSION })),
    landmarks: [],
    obstacles: [],
    zones: [
      { id: 'pinball-arena', type: 'collision', start: .24, end: .54, strength: 1, label: '핀 아레나' },
      { id: 'pinball-finish', type: 'finish', start: .9, end: 1, strength: .18 },
    ],
    cameraZones: [
      { id: 'start', start: 0, end: .14, zoom: 1.06 },
      { id: 'collision', start: .18, end: .6, zoom: 1.2 },
      { id: 'finish', start: .84, end: 1, zoom: 1.34 },
    ],
    eventPool: ['flipper-frenzy', 'drop-close', 'pin-sink'],
    field: PINBALL_V3_FIELD,
  },
  {
    id: 'lift',
    version: 'lift-v1.5',
    title: '리프트 터미널',
    watchPoint: '리프트 선택',
    description: '정원과 주기가 다른 리프트를 갈아타고 꼭대기 결승 터미널까지 오르는 상승 맵',
    background: '#0a0f26',
    track: '#39418c',
    accent: '#3edbff',
    worldWidth: 1100,
    worldHeight: 1500,
    firstFinishSeconds: 32,
    lastFinishSeconds: 90,
    path: [
      { x: 470, y: 1290, width: 300 }, { x: 380, y: 1400, width: 200 },
      { x: 288, y: 1408, width: 120 }, { x: 288, y: 1200, width: 120 },
      { x: 288, y: 1060, width: 120 }, { x: 250, y: 1010, width: 200 },
      { x: 330, y: 1052, width: 200 }, { x: 412, y: 1050, width: 90 },
      { x: 412, y: 860, width: 90 }, { x: 412, y: 715, width: 90 },
      { x: 500, y: 715, width: 200 }, { x: 640, y: 712, width: 200 },
      { x: 790, y: 708, width: 200 }, { x: 870, y: 700, width: 90 },
      { x: 953, y: 700, width: 80 }, { x: 953, y: 540, width: 80 },
      { x: 953, y: 430, width: 80 }, { x: 900, y: 350, width: 180 },
      { x: 860, y: 300, width: 180 },
    ],
    landmarks: [],
    obstacles: [],
    zones: [
      { id: 'lift-hall', type: 'collision', start: .55, end: .76, strength: 1, label: '가로 통로' },
      { id: 'lift-finish', type: 'finish', start: .9, end: 1, strength: .18 },
    ],
    cameraZones: [
      { id: 'start', start: 0, end: .18, zoom: 1.02 },
      { id: 'mid', start: .25, end: .68, zoom: 1.12 },
      { id: 'finish', start: .85, end: 1, zoom: 1.28 },
    ],
    eventPool: ['lift-express', 'early-dump', 'rotor-stop'],
    field: FIELD_SPECS.lift,
  },
  {
    id: 'belt-yard',
    version: 'belt-yard-v3.2',
    title: '다중 벨트 야드',
    watchPoint: '3속 벨트·혼합 풀',
    description: '속도가 다른 병렬 벨트 3뱅크와 혼합 풀, 역주행 패치가 상시 추월을 만드는 v3 확장 컨베이어',
    background: '#0a0f26',
    track: '#39418c',
    accent: '#ff5ca8',
    worldWidth: 1756,
    worldHeight: 1222,
    firstFinishSeconds: 45,
    lastFinishSeconds: 52,
    path: beltYardGuidePath(),
    landmarks: [],
    obstacles: [],
    zones: [
      { id: 'by-pool', type: 'collision', start: .26, end: .44, strength: 1, label: '혼합 풀' },
      { id: 'by-yard', type: 'collision', start: .6, end: .72, strength: 1, label: '전환 야드' },
      { id: 'by-finish', type: 'finish', start: .93, end: 1, strength: .18 },
    ],
    cameraZones: [
      { id: 'start', start: 0, end: .14, zoom: 1.04 },
      { id: 'pool', start: .26, end: .44, zoom: 1.14 },
      { id: 'yard', start: .58, end: .72, zoom: 1.12 },
      { id: 'finish', start: .86, end: 1, zoom: 1.28 },
    ],
    eventPool: ['belt-reverse', 'patch-expand', 'gate-frenzy'],
    field: FIELD_SPECS['belt-yard'],
  },
  {
    id: 'grand-vortex',
    version: 'grand-vortex-v3.3',
    title: '대소용돌이',
    watchPoint: '컷 게이트·배수 홀',
    description: '안쪽이 더 넓어지는 나선 2바퀴를 컷 게이트 도박으로 건너뛰고 중앙 배수 홀에 먼저 빠지는 v3 확장 소용돌이',
    background: '#0a0f26',
    track: '#39418c',
    accent: '#b79cff',
    worldWidth: 1222,
    worldHeight: 1289,
    firstFinishSeconds: 48,
    lastFinishSeconds: 56,
    path: grandVortexGuidePath(),
    landmarks: [],
    obstacles: [],
    zones: [
      { id: 'gv-spiral', type: 'collision', start: .3, end: .78, strength: 1, label: '나선' },
      { id: 'gv-arena', type: 'collision', start: .86, end: .97, strength: 1, label: '중앙 아레나' },
      { id: 'gv-finish', type: 'finish', start: .97, end: 1, strength: .18 },
    ],
    cameraZones: [
      { id: 'start', start: 0, end: .12, zoom: 1.02 },
      { id: 'spiral', start: .14, end: .84, zoom: .98 },
      { id: 'finish', start: .88, end: 1, zoom: 1.3 },
    ],
    eventPool: ['disk-reverse', 'cut-open', 'boost-ring'],
    field: FIELD_SPECS['grand-vortex'],
  },
  {
    id: 'canyon',
    version: 'canyon-v3.3',
    title: '협곡 종주',
    watchPoint: '분기 도박·페그 셔플',
    description: '부스터 직선과 뱅크턴 3개를 지나 3레인 분기 도박과 결승 직전 페그 필드가 순위를 뒤집는 v3 롱 서펜타인',
    background: '#0a0f26',
    track: '#39418c',
    accent: '#b8e94a',
    worldWidth: 1556,
    worldHeight: 1111,
    firstFinishSeconds: 46,
    lastFinishSeconds: 54,
    path: canyonGuidePath(),
    landmarks: [],
    obstacles: [],
    zones: [
      { id: 'cy-bowl', type: 'collision', start: .3, end: .44, strength: 1, label: '충돌 사발' },
      { id: 'cy-branch', type: 'collision', start: .55, end: .68, strength: 1, label: '분기 도박' },
      { id: 'cy-pegs', type: 'collision', start: .78, end: .9, strength: 1, label: '페그 필드' },
      { id: 'cy-finish', type: 'finish', start: .93, end: 1, strength: .18 },
    ],
    cameraZones: [
      { id: 'start', start: 0, end: .12, zoom: 1.05 },
      { id: 'bowl', start: .3, end: .45, zoom: 1.18 },
      { id: 'branch', start: .55, end: .68, zoom: 1.16 },
      { id: 'finish', start: .85, end: 1, zoom: 1.3 },
    ],
    eventPool: ['gate-frenzy', 'boost-surge', 'drop-close'],
    field: FIELD_SPECS.canyon,
  },
  {
    id: 'tower',
    version: 'tower-v3.7',
    title: '나선 하강 타워',
    watchPoint: '결승 회전봉·막판 경합',
    description: '연속 낙하와 두 갈래 경사를 지나, 결승 회전봉에서 다시 모여 마지막까지 순위를 겨루는 중력 타워',
    background: '#0a0f26',
    track: '#39418c',
    accent: '#3edbff',
    worldWidth: 889,
    worldHeight: 3667,
    firstFinishSeconds: 42,
    lastFinishSeconds: 50,
    path: towerGuidePath(),
    landmarks: [],
    obstacles: [],
    zones: [
      { id: 'tw-return', type: 'collision', start: .12, end: .22, strength: 1, label: '반환 사발' },
      { id: 'tw-pachinko', type: 'collision', start: .32, end: .48, strength: 1, label: '파친코 낙하' },
      { id: 'tw-bowl', type: 'collision', start: .57, end: .7, strength: 1, label: '충돌 사발' },
      { id: 'tw-finish', type: 'finish', start: .94, end: 1, strength: .18 },
    ],
    cameraZones: [
      { id: 'start', start: 0, end: .08, zoom: .95 },
      { id: 'tower', start: .08, end: .85, zoom: 1.08 },
      { id: 'finish', start: .88, end: 1, zoom: 1.28 },
    ],
    eventPool: ['gate-frenzy', 'rotor-reverse', 'gravity-surge'],
    field: FIELD_SPECS.tower,
    finishCamera: { entryY: 5700 * 10 / 18, x: 900 * 10 / 18, y: 6100 * 10 / 18, width: 340, height: 330 },
    revealOnTargetFinish: true,
  },
  {
    id: 'gravity-bowls',
    version: 'gravity-bowls-v3.1',
    title: '중력 순환 사발',
    watchPoint: '8개 사발·중력 회전',
    description: '중력 방향이 바뀌는 8개의 둥근 사발을 지나, 범퍼로 가득한 중앙 아레나에서 결승을 겨룹니다.',
    background: '#0a0f26', track: '#39418c', accent: '#b79cff',
    worldWidth: 1945, worldHeight: 1945,
    firstFinishSeconds: 46, lastFinishSeconds: 54,
    path: gravityBowlsGuidePath(), landmarks: [], obstacles: [],
    zones: [
      { id: 'gb-b', type: 'collision', start: .1, end: .16, strength: 1, label: '범퍼 광장' },
      { id: 'gb-d', type: 'collision', start: .35, end: .41, strength: 1, label: '페그 셔플' },
      { id: 'gb-g', type: 'slow', start: .71, end: .79, strength: .6, label: '슬로우 링' },
      { id: 'gb-finish', type: 'finish', start: .94, end: 1, strength: .18 },
    ],
    cameraZones: [
      { id: 'start', start: 0, end: .04, zoom: 1.08 },
      { id: 'circuit', start: .04, end: .88, zoom: 1.12 },
      { id: 'finish', start: .88, end: 1, zoom: 1.3 },
    ],
    minimumCameraZoom: .1,
    overviewCamera: { start: .04, end: .88, x: 972.5, y: 972.5, width: 1945, height: 1945 },
    eventPool: ['gravity-turn', 'drum-reverse', 'slow-ring-release'],
    field: FIELD_SPECS['gravity-bowls'],
  },
  {
    id: 'ring-circuit', version: 'ring-circuit-v3.3', title: '환상 서킷',
    watchPoint: '2랩·인필드 지름길',
    description: '두 바퀴를 달리는 순환 서킷. 2랩에서만 열리는 범퍼 지름길과 마지막 결승 분기가 순위를 바꿔요.',
    background: '#0a0f26', track: '#39418c', accent: '#a888ff',
    worldWidth: 1445, worldHeight: 1040,
    firstFinishSeconds: 56, lastFinishSeconds: 66,
    path: ringCircuitGuidePath(), landmarks: [], obstacles: [], zones: [],
    cameraZones: [{ id: 'start', start: 0, end: .1, zoom: .9 }, { id: 'laps', start: .1, end: .93, zoom: 1.35 }, { id: 'finish', start: .93, end: 1, zoom: 1.5 }],
    eventPool: ['boost-surge', 'circuit-cut-open', 'circuit-pin-sink'],
    field: RING_CIRCUIT_FIELD,
  },
] as const;

export const PLAYABLE_MAP_IDS = ['canyon', 'tower', 'ring-circuit', 'grand-vortex', 'belt-yard', 'gravity-bowls', 'pinball'] as const satisfies readonly FateMapId[];
export const DEFAULT_FATE_MAP_ID = PLAYABLE_MAP_IDS[0];
export const FATE_MAPS: readonly FateMapDefinition[] = PLAYABLE_MAP_IDS.map(mapById);
export const ARCHIVED_FATE_MAPS: readonly FateMapDefinition[] = ALL_FATE_MAPS.filter((map) => !FATE_MAPS.includes(map));

/** 회귀 검사는 선택 화면에서 보관 처리한 맵도 계속 포함한다. */
export const LEGACY_FATE_MAPS: readonly FateMapDefinition[] = ALL_FATE_MAPS.filter((map) => !map.field);
export const FIELD_FATE_MAPS: readonly FateMapDefinition[] = ALL_FATE_MAPS.filter((map) => map.field);

/** 리플레이가 기록해야 하는 움직이는 장치 채널 id 목록. */
export function replayChannelIds(map: FateMapDefinition): readonly string[] {
  if (map.field) return fieldChannelIds(map.field);
  return map.obstacles.filter((obstacle) => obstacle.type === 'spinner').map((obstacle) => obstacle.id);
}

export function mapById(id: FateMapId): FateMapDefinition {
  const map = ALL_FATE_MAPS.find((candidate) => candidate.id === id);
  if (!map) throw new Error(`알 수 없는 맵입니다: ${id}`);
  return map;
}

/** 새 경기에서 오래된/알 수 없는 선택값은 기본 맵으로 안전하게 되돌린다. */
export function normalizeMapSelection(selection: string | null): FateMapSelection {
  if (selection === 'random') return selection;
  return FATE_MAPS.find((map) => map.id === selection)?.id ?? DEFAULT_FATE_MAP_ID;
}

export function resolveMap(selection: FateMapSelection, seed: string): FateMapDefinition {
  const currentSelection = normalizeMapSelection(selection);
  if (currentSelection !== 'random') return mapById(currentSelection);
  const random = seededRandom(seed, 'map');
  return FATE_MAPS[Math.floor(random() * FATE_MAPS.length)];
}

export interface TrackSample {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly distance: number;
  readonly progress: number;
  readonly angle: number;
  readonly tangentX: number;
  readonly tangentY: number;
  readonly normalX: number;
  readonly normalY: number;
}

const sampleCache = new WeakMap<readonly TrackPoint[], readonly TrackSample[]>();

function catmullRom(a: number, b: number, c: number, d: number, t: number): number {
  const t2 = t * t;
  const t3 = t2 * t;
  return .5 * ((2 * b) + (-a + c) * t + (2 * a - 5 * b + 4 * c - d) * t2 + (-a + 3 * b - 3 * c + d) * t3);
}

export function trackSamples(path: readonly TrackPoint[]): readonly TrackSample[] {
  const cached = sampleCache.get(path);
  if (cached) return cached;
  const rough: Array<{ x: number; y: number; width: number; distance: number }> = [];
  let distance = 0;
  for (let segment = 0; segment < path.length - 1; segment += 1) {
    const p0 = path[Math.max(0, segment - 1)];
    const p1 = path[segment];
    const p2 = path[segment + 1];
    const p3 = path[Math.min(path.length - 1, segment + 2)];
    const steps = 32;
    for (let step = segment === 0 ? 0 : 1; step <= steps; step += 1) {
      const t = step / steps;
      const x = catmullRom(p0.x, p1.x, p2.x, p3.x, t);
      const y = catmullRom(p0.y, p1.y, p2.y, p3.y, t);
      const width = (p1.width ?? 140) + ((p2.width ?? 140) - (p1.width ?? 140)) * t;
      const previous = rough.at(-1);
      if (previous) distance += Math.hypot(x - previous.x, y - previous.y);
      rough.push({ x, y, width, distance });
    }
  }
  const total = Math.max(1, distance);
  const samples: TrackSample[] = rough.map((point, index) => {
    const previous = rough[Math.max(0, index - 1)];
    const next = rough[Math.min(rough.length - 1, index + 1)];
    const length = Math.max(.001, Math.hypot(next.x - previous.x, next.y - previous.y));
    const tangentX = (next.x - previous.x) / length;
    const tangentY = (next.y - previous.y) / length;
    return {
      ...point,
      progress: point.distance / total,
      angle: Math.atan2(tangentY, tangentX),
      tangentX,
      tangentY,
      normalX: -tangentY,
      normalY: tangentX,
    };
  });
  sampleCache.set(path, samples);
  return samples;
}

export function trackLength(path: readonly TrackPoint[]): number {
  return trackSamples(path).at(-1)?.distance ?? 0;
}

function sampleAtDistance(path: readonly TrackPoint[], distance: number): TrackSample {
  const samples = trackSamples(path);
  const target = Math.max(0, Math.min(trackLength(path), distance));
  let low = 0;
  let high = samples.length - 1;
  while (low < high) {
    const middle = Math.floor((low + high) / 2);
    if (samples[middle].distance < target) low = middle + 1;
    else high = middle;
  }
  const next = samples[low];
  const previous = samples[Math.max(0, low - 1)];
  const span = Math.max(.001, next.distance - previous.distance);
  const amount = Math.max(0, Math.min(1, (target - previous.distance) / span));
  const tangentX = previous.tangentX + (next.tangentX - previous.tangentX) * amount;
  const tangentY = previous.tangentY + (next.tangentY - previous.tangentY) * amount;
  const tangentLength = Math.max(.001, Math.hypot(tangentX, tangentY));
  const tx = tangentX / tangentLength;
  const ty = tangentY / tangentLength;
  return {
    x: previous.x + (next.x - previous.x) * amount,
    y: previous.y + (next.y - previous.y) * amount,
    width: previous.width + (next.width - previous.width) * amount,
    distance: target,
    progress: target / Math.max(1, trackLength(path)),
    angle: Math.atan2(ty, tx),
    tangentX: tx,
    tangentY: ty,
    normalX: -ty,
    normalY: tx,
  };
}

export interface PathPose extends TrackSample {}

export function pointOnPath(path: readonly TrackPoint[], progress: number, laneOffset = 0): PathPose {
  const pose = sampleAtDistance(path, Math.max(0, Math.min(1, progress)) * trackLength(path));
  return {
    ...pose,
    x: pose.x + pose.normalX * laneOffset,
    y: pose.y + pose.normalY * laneOffset,
  };
}

export function pointAtTrackDistance(path: readonly TrackPoint[], distance: number, laneOffset = 0): PathPose {
  const pose = sampleAtDistance(path, distance);
  return {
    ...pose,
    x: pose.x + pose.normalX * laneOffset,
    y: pose.y + pose.normalY * laneOffset,
  };
}
